import React, { Component } from 'react';
import { connect } from 'react-redux';
import { SubmissionError } from 'redux-form';
import Breadcrumb from '../../common/components/Breadcrumb';
import Loading from '../../common/components/Loading';
import EditVideoForm from './EditVideoForm';
import dataUrlToFile from '../../common/utils/dataUrlToFile';
import VideoPlayer from '../../common/video/VideoPlayer';
import cloneDeep from 'lodash/cloneDeep';

import { formatValidationErrors } from '../../common/utils/getApiReducer';
import { fetchCourses } from '../../courses/actions';

import {
  fetchSection,
  uploadVideo,
  getLibraryRecord,
  updateLibraryRecord,
  fetchCourseLessons,
  clearCourseLessons,
} from '../actions';

import { selectActiveCategory, selectActiveRecord } from '../selectors';

class EditVideoPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      clearOrigImage: false,
      editing: false,
    };
  }

  componentDidMount() {
    const { match, activeSection, dispatch, coursesLoaded, activeRecord } = this.props;

    if (!activeSection) {
      dispatch(fetchSection(match.params.categoryId, match.params.sectionId));
    }

    if (!coursesLoaded) {
      dispatch(fetchCourses());
    }

    if (activeRecord) {
      this.loadLessons(activeRecord);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { activeRecord } = this.props;

    if (!activeRecord && nextProps.activeRecord) {
      this.loadLessons(nextProps.activeRecord);
    }
  }

  loadLessons(record) {
    if (record.get('pre_requisite_course_id')) {
      this.props.dispatch(fetchCourseLessons(record.get('pre_requisite_course_id')));
    } else {
      this.props.dispatch(clearCourseLessons());
    }
  }

  toggleEdit = () => {
    this.setState(state => ({
      editing: !state.editing,
      clearOrigImage: false,
    }));
  };

  onSubmit = formValues => {
    const { activeRecord, dispatch, match: { params: { categoryId, sectionId, videoId } } } = this.props;

    const values = cloneDeep(formValues);

    if (values.image) {
      values.image = dataUrlToFile(values.image.imageResult, values.image.imageFilename);
    }

    if (!this.state.clearOrigImage && activeRecord.get('image_id')) {
      values.image_id = activeRecord.get('image_id');
    }

    if (values.pre_requisite_lesson_id === '' || !values.pre_requisite_course_id) {
      values.pre_requisite_course_id = null;
      values.pre_requisite_lesson_id = null;
    }

    if (values.description === null) {
      values.description = undefined;
    }

    return dispatch(updateLibraryRecord(categoryId, sectionId, videoId, values)).then(action => {
      if (!action.response.ok) {
        throw new SubmissionError(formatValidationErrors(action.json).toJS());
      } else {
        this.setState({ editing: false });
      }
    });
  };

  onCourseChange = e => {
    if (e.target.value.length) {
      this.props.dispatch(fetchCourseLessons(e.target.value));
    } else {
      this.props.dispatch(clearCourseLessons());
    }
  };

  checkVideoStatus = () => {
    const { dispatch, match: { params: { categoryId, sectionId, videoId } } } = this.props;

    dispatch(getLibraryRecord(categoryId, sectionId, videoId)).then(this.handleVideoResponse);
  };

  handleVideoResponse = response => {
    if (
      response.json &&
      response.json.video.transcode_status !== 'Complete' &&
      response.json.video.transcode_status !== 'Error'
    ) {
      setTimeout(this.checkVideoStatus, 10000);
    }
  };

  onVideoUpload = file => {
    const { dispatch, match: { params: { categoryId, sectionId, videoId } } } = this.props;

    return dispatch(uploadVideo(categoryId, sectionId, videoId, file[0])).then(response => {
      setTimeout(this.checkVideoStatus, 10000);
    });
  };

  onRemoveImage = () => {
    this.setState(state => ({
      clearOrigImage: true,
    }));
  };

  render() {
    const {
      match,
      activeCategory,
      activeSection,
      activeRecord,
      courses,
      coursesLoaded,
      lessons,
      videoUploading,
      videoUploadProgress,
    } = this.props;

    const { editing } = this.state;

    if (!activeSection || !coursesLoaded) {
      return <Loading />;
    }

    const breadcrumbs = [
      {
        label: 'Library',
        to: '/library',
      },
      {
        label: activeCategory.get('name'),
        to: `/library/${match.params.categoryId}`,
      },
      {
        label: activeSection.get('name'),
        to: `/library/${match.params.categoryId}/sections/${match.params.sectionId}`,
      },
      {
        label: activeRecord.get('name'),
        to: null,
      },
    ];

    const initialFormValues = {
      description: activeRecord.get('description'),
      name: activeRecord.get('name'),
      active: activeRecord.get('active'),
    };

    if (activeRecord.get('pre_requisite_course_id')) {
      initialFormValues.pre_requisite_course_id = activeRecord.get('pre_requisite_course_id');
    }

    if (activeRecord.get('pre_requisite_lesson_id')) {
      initialFormValues.pre_requisite_lesson_id = activeRecord.get('pre_requisite_lesson_id');
    }

    const courseName = activeRecord.get('pre_requisite_course_id')
      ? courses.find(course => course.get('id') === activeRecord.get('pre_requisite_course_id')).get('name')
      : 'None';

    let lessonName = 'None';

    if (activeRecord.get('pre_requisite_lesson_id') && lessons) {
      const lesson = lessons.find(lesson => lesson.get('id') === activeRecord.get('pre_requisite_lesson_id'));

      if (lesson) {
        lessonName = lesson.get('name');
      }
    }

    return (
      <div className="admin-paper">
        <div className="admin-paper__header">
          <Breadcrumb crumbs={breadcrumbs} />
        </div>
        <div className="admin-paper__edit-content">
          {editing ? (
            <EditVideoForm
              initialValues={initialFormValues}
              activeRecord={activeRecord}
              lessons={lessons}
              courses={courses}
              onSubmit={this.onSubmit}
              onCancel={this.toggleEdit}
              handleVideoUpload={this.onVideoUpload}
              handleCourseChange={this.onCourseChange}
              handleRemoveImage={this.onRemoveImage}
              videoUploading={videoUploading}
              videoUploadProgress={videoUploadProgress}
              clearOrigImage={this.state.clearOrigImage}
            />
          ) : (
            <div>
              <div className="admin-paper__edit-content__row">
                <p>Video Name</p>
                <span className="title">{activeRecord.get('name')}</span>
              </div>
              <div className="admin-paper__edit-content__row">
                <p>Video Description</p>
                <span>
                  {activeRecord.get('description') ? activeRecord.get('description') : <em>No Description</em>}
                </span>
              </div>
              <div className="admin-paper__edit-content__row">
                <p>Required Course</p>
                <span>{courseName}</span>
              </div>
              <div className="admin-paper__edit-content__row">
                <p>Required Lesson</p>
                <span>{lessonName}</span>
              </div>
              <div className="admin-paper__edit-content__row">
                <div className="admin-paper__edit-content__upload">
                  <p>Video</p>
                  {activeRecord.get('video') ? (
                    <div className="admin__image-preview">
                      <VideoPlayer
                        video={activeRecord.get('video')}
                        key={`${activeRecord.getIn(['video', 'key'])}-${activeRecord.getIn([
                          'video',
                          'transcode_status',
                        ])}`}
                      />
                    </div>
                  ) : (
                    <div className="admin__image__placeholder image__placeholder">No Video</div>
                  )}
                </div>
                <div className="admin-paper__edit-content__upload">
                  <p>Video Thumbnail</p>
                  {activeRecord.getIn(['image', 'location']) ? (
                    <div
                      className="admin__image-preview"
                      style={{ backgroundImage: `url(${activeRecord.getIn(['image', 'location'])})` }}
                    />
                  ) : (
                    <div className="admin__image__placeholder image__placeholder">No Image</div>
                  )}
                </div>
              </div>
              <div className="admin-paper__edit-button">
                <button className="button" onClick={this.toggleEdit}>
                  Edit
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default connect((state, ownProps) => {
  const activeCategory = selectActiveCategory(state.library.categories, ownProps.match.params.categoryId);
  const activeRecord = selectActiveRecord(state.library.activeSection, ownProps.match.params.videoId);

  return {
    activeRecord,
    activeCategory,
    activeSection: state.library.activeSection.get('data'),
    courses: state.courses.courses.get('data'),
    coursesLoaded: state.courses.courses.get('loaded'),
    lessons: state.library.courseLessons.get('data'),
    videoUploading: state.library.videoUploading,
    videoUploadProgress: state.library.videoUploadProgress,
  };
})(EditVideoPage);



// WEBPACK FOOTER //
// ./src/library/video/EditVideoPage.js